// buildNarrativeEcosystem.js
// Detecta narrativas territoriales desde múltiples fuentes
// con pesos según legitimidad del actor y tipo de fuente

export function buildNarrativeEcosystem({
  actors = [],
  alerts = [],
  signals = [],
  timeline = []
}) {
  const conteo = {};

  // ─────────────────────────────────────────────
  // FUENTE 1 — ACTORES
  // Peso según influencia y centralidad relacional
  // ─────────────────────────────────────────────
  for (const actor of actors) {
    const pesoBase = getPesoInfluencia(actor.influencia);
    const centralidad = actor._vita?.relacional?.centralidad || 0;
    const multiplicador = 1 + (centralidad / 100) * 0.5;
    const peso = pesoBase * multiplicador;

    // Narrativas declaradas como array (nuevo modelo)
    const narrativas = Array.isArray(actor.narrativas) ? actor.narrativas : [];
    for (const narrativa of narrativas) {
      const txt = typeof narrativa === 'string' ? narrativa.trim() : (narrativa?.texto || '').trim();
      if (txt) acumular(conteo, txt, peso, 'actor', actor.nombre);
    }

    // Narrativa dominante declarada (modelo anterior — compatibilidad)
    if (actor.narrativaDominante?.trim()) {
      acumular(conteo, actor.narrativaDominante.trim(), peso * 1.2, 'actor', actor.nombre);
    }
  }

  // ─────────────────────────────────────────────
  // FUENTE 2 — ALERTAS
  // Peso fijo según nivel de alerta
  // ─────────────────────────────────────────────
  for (const alerta of alerts) {
    const peso = getPesoAlerta(alerta.nivel);
    const texto = alerta.titulo || alerta.descripcion;
    if (texto?.trim()) {
      acumular(conteo, texto.trim(), peso, 'alerta', alerta.titulo);
    }
  }

  // ─────────────────────────────────────────────
  // FUENTE 3 — SEÑALES RRSS
  // Peso bajo — un trol no equivale a un actor legítimo
  // ─────────────────────────────────────────────
  for (const signal of signals) {
    const peso = getPesoSignal(signal.sentimiento, signal.riesgo);
    const texto = signal.texto || signal.descripcion;
    if (texto?.trim()) {
      // No usar el texto completo — demasiado específico
      // Solo si tiene narrativa explícita declarada
      if (signal.narrativaDominante?.trim()) {
        acumular(conteo, signal.narrativaDominante.trim(), peso, 'rrss', signal.red);
      }
    }
  }

  // ─────────────────────────────────────────────
  // FUENTE 4 — TIMELINE
  // Peso bajo — registro de gestión, no narrativa
  // ─────────────────────────────────────────────
  for (const evento of timeline) {
    if (evento.narrativaDominante?.trim()) {
      acumular(conteo, evento.narrativaDominante.trim(), 1, 'timeline', evento.titulo);
    }
  }

  // ─────────────────────────────────────────────
  // CONSTRUIR RESULTADO
  // ─────────────────────────────────────────────
  const ranked = Object.values(conteo)
    .sort((a, b) => b.score - a.score);

  if (ranked.length === 0) {
    return {
      dominante: 'Sin narrativa territorial detectada',
      secundarias: [],
      emergentes: [],
      relato: 'Sin relato territorial consolidado. Se requieren narrativas declaradas por actores.',
      convergencia: 'Sin convergencia narrativa'
    };
  }

  const dominante = ranked[0].label;

  const secundarias = ranked
    .slice(1, 4)
    .map(n => n.label);

  // Emergentes: narrativas con score bajo pero de múltiples fuentes
  const emergentes = ranked
    .filter(n => n.fuentes.size >= 2 && n.score < ranked[0].score * 0.5)
    .slice(0, 3)
    .map(n => n.label);

  // Relato territorial: síntesis de las narrativas más fuertes
  const relato = buildRelato(ranked);

  return {
    dominante,
    secundarias,
    emergentes,
    relato,
    convergencia: buildConvergencia(ranked),
    scored: ranked // Para debugging y análisis
  };
}

// ─────────────────────────────────────────────
// ACUMULADOR DE NARRATIVAS
// ─────────────────────────────────────────────

function acumular(conteo, texto, peso, tipo, fuente) {
  // Normalizar para agrupar similares
  const key = texto.toLowerCase().trim();

  if (!conteo[key]) {
    conteo[key] = {
      label: texto,
      score: 0,
      fuentes: new Set(),
      tipos: new Set()
    };
  }

  conteo[key].score += peso;
  conteo[key].fuentes.add(fuente);
  conteo[key].tipos.add(tipo);
}

// ─────────────────────────────────────────────
// PESOS POR TIPO DE FUENTE
// ─────────────────────────────────────────────

function getPesoInfluencia(influencia) {
  const inf = String(influencia || '').toLowerCase();
  if (inf === 'alta') return 5;
  if (inf === 'media') return 3;
  if (inf === 'baja') return 1;
  return 2;
}

function getPesoAlerta(nivel) {
  const n = String(nivel || '').toLowerCase();
  if (n === 'crítico' || n === 'critico') return 3;
  if (n === 'alto' || n === 'alta') return 2;
  if (n === 'medio' || n === 'media') return 1.5;
  return 1;
}

function getPesoSignal(sentimiento, riesgo) {
  const sent = String(sentimiento || '').toLowerCase();
  const ries = String(riesgo || '').toLowerCase();

  if (sent === 'crítico' || ries === 'crítico') return 1.5;
  if (sent === 'negativo' || ries === 'alto') return 1;
  return 0.5;
}

// ─────────────────────────────────────────────
// CONSTRUCCIÓN DEL RELATO TERRITORIAL
// Síntesis de las narrativas más fuertes
// ─────────────────────────────────────────────

function buildRelato(ranked) {
  if (ranked.length === 0) return 'Sin relato territorial consolidado.';

  const top = ranked.slice(0, 3);
  const dominante = top[0];

  if (top.length === 1) {
    return `El territorio articula una narrativa central: "${dominante.label}". ` +
      `Con un peso de ${Math.round(dominante.score)} puntos territoriales.`;
  }

  const secundarias = top.slice(1).map(n => `"${n.label}"`).join(' y ');

  return `El territorio articula como narrativa dominante "${dominante.label}", ` +
    `convergiendo con ${secundarias}. ` +
    `Esta convergencia sugiere un relato territorial en construcción.`;
}

function buildConvergencia(ranked) {
  if (ranked.length === 0) return 'Sin convergencia narrativa';

  const top = ranked[0];
  const cantidadFuentes = top.fuentes.size;

  if (cantidadFuentes >= 4) return `Alta convergencia — ${cantidadFuentes} actores comparten la narrativa dominante`;
  if (cantidadFuentes >= 2) return `Convergencia media — ${cantidadFuentes} fuentes coinciden en narrativa dominante`;
  return 'Convergencia baja — narrativa presente en una sola fuente';
}